import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export const initCascadeScene = () => {
	const scenes = gsap.utils.toArray(".cascade-scene");
	if (!scenes.length) return;

	gsap.registerPlugin(ScrollTrigger);

	scenes.forEach((scene) => {
		const photos = gsap.utils.toArray(scene.querySelectorAll(".cascade-item"));
		if (photos.length < 2) return;

		let layoutOffsets = [];
		let rowCount = 1;
		let maxLayoutOffset = 0;

		const getNumericValue = (value, fallback) => {
			const parsed = Number.parseFloat(value);
			return Number.isFinite(parsed) ? parsed : fallback;
		};

		const getStepOffset = () => {
			const isMobile = window.innerWidth < 768;
			const defaultStep = isMobile ? 18 : 56;
			const attrStep = isMobile ? scene.dataset.cascadeStepMobile : scene.dataset.cascadeStep;
			const step = getNumericValue(attrStep, defaultStep);
			const intensity = getNumericValue(scene.dataset.cascadeIntensity, 1);
			return step * intensity;
		};

		const getLeadOffset = () => {
			const stepOffset = getStepOffset();
			const leadMultiplier = getNumericValue(scene.dataset.cascadeLead, 0.45);
			return stepOffset * leadMultiplier;
		};

		const getRowStepOffset = () => {
			const stepOffset = getStepOffset();
			const rowStepMultiplier = getNumericValue(scene.dataset.cascadeRowStep, 0.75);
			return stepOffset * rowStepMultiplier;
		};

		const updateLayoutOffsets = () => {
			const rowTops = [];
			const rowColumnCounts = [];
			const offsets = [];
			const rowTolerance = 4;
			const leadOffset = getLeadOffset();
			const stepOffset = getStepOffset();
			const rowStepOffset = getRowStepOffset();

			photos.forEach((photo, index) => {
				const photoTop = photo.offsetTop;
				let rowIndex = rowTops.findIndex((rowTop) => Math.abs(rowTop - photoTop) <= rowTolerance);

				if (rowIndex === -1) {
					rowTops.push(photoTop);
					rowColumnCounts.push(0);
					rowIndex = rowTops.length - 1;
				}

				const columnIndex = rowColumnCounts[rowIndex];
				rowColumnCounts[rowIndex] += 1;
				offsets[index] = leadOffset + columnIndex * stepOffset + rowIndex * rowStepOffset;
			});

			layoutOffsets = offsets;
			rowCount = Math.max(1, rowTops.length);
			maxLayoutOffset = offsets.length ? Math.max(...offsets) : 0;
		};

		const getStartPoint = () => {
			const startViewportPercent = getNumericValue(scene.dataset.cascadeStart, 78);
			return `top ${startViewportPercent}%`;
		};

		const getEndPoint = () => {
			const flattenViewportPercent = getNumericValue(scene.dataset.cascadeFlattenTop, 28);
			const rowEndAdjustment = getNumericValue(scene.dataset.cascadeRowEndAdjust, 10);
			const endViewportPercent = flattenViewportPercent - Math.max(0, rowCount - 1) * rowEndAdjustment;
			return `top ${Math.max(-80, endViewportPercent)}%`;
		};

		const updateSceneHeight = () => {
			updateLayoutOffsets();
			const tallestPhoto = Math.max(...photos.map((photo) => photo.getBoundingClientRect().height));
			const buffer = Math.max(16, getStepOffset() * 0.35);
			const minHeightAdjustment = getNumericValue(scene.dataset.cascadeMinHeightAdjust, 0);
			const minHeight = tallestPhoto + maxLayoutOffset + buffer + minHeightAdjustment;
			scene.style.minHeight = `${Math.ceil(Math.max(0, minHeight))}px`;
		};

		const applyInitialOffsets = () => {
			updateLayoutOffsets();
			gsap.set(photos, {
				y: (index) => layoutOffsets[index] || 0,
				force3D: true,
			});
		};

		updateSceneHeight();
		applyInitialOffsets();

		gsap.to(photos, {
			y: 0,
			ease: "none",
			lazy: false,
			stagger: 0.035,
			scrollTrigger: {
				trigger: scene,
				start: getStartPoint,
				end: getEndPoint,
				scrub: true,
				onRefreshInit: () => {
					updateSceneHeight();
					applyInitialOffsets();
				},
				onRefresh: updateSceneHeight,
				invalidateOnRefresh: true,
			},
		});

		window.addEventListener("load", updateSceneHeight, { once: true });
	});
};
